import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';
import { Button } from './button';
import { cn } from './utils';
import { COUNTRIES_SORTED, countryFlag } from '../../src/utils/countries';

interface CountryComboboxProps {
  value: string;          // ISO 3166-1 alpha-2 code
  onChange: (code: string) => void;
  placeholder?: string;
  required?: boolean;
  error?: boolean;
}

export function CountryCombobox({
  value,
  onChange,
  placeholder = 'Select country…',
  error = false,
}: CountryComboboxProps) {
  const [open, setOpen] = useState(false);

  const selected = COUNTRIES_SORTED.find((c) => c.code === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-full justify-between font-normal',
            !selected && 'text-muted-foreground',
            error && 'border-destructive focus-visible:ring-destructive'
          )}
        >
          {selected ? (
            <span className="flex items-center gap-2 truncate">
              <span className="text-base leading-none">{countryFlag(selected.code)}</span>
              <span>{selected.name}</span>
            </span>
          ) : (
            placeholder
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search country…" />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {COUNTRIES_SORTED.map((country) => (
                <CommandItem
                  key={country.code}
                  value={`${country.name} ${country.code}`}
                  onSelect={() => {
                    onChange(country.code);
                    setOpen(false);
                  }}
                >
                  <span className="flex items-center gap-2 flex-1 truncate">
                    <span className="text-base leading-none">{countryFlag(country.code)}</span>
                    <span>{country.name}</span>
                    <span className="ml-auto text-xs text-muted-foreground shrink-0">
                      {country.currency}
                    </span>
                  </span>
                  <Check
                    className={cn(
                      'ml-2 h-4 w-4 shrink-0',
                      value === country.code ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
